/**
 * `Hook_de_Telemetria` — the React lifecycle layer of the Snap Reports
 * telemetry stack.
 *
 * Feature `telemetry-observability`, task 5.3 (Requirements 11.3, 11.9; design
 * component 8, "Hook_de_Telemetria"). This hook owns **only** React
 * lifecycle/timer state: it measures flow durations in milliseconds between a
 * start and an end marker and, critically, cancels every pending timer and
 * discards every scheduled emission when the host component unmounts, so that
 * **no telemetry event is ever emitted after unmount** (Req 11.9).
 *
 * It stays intentionally thin: all actual emission is delegated to the pure
 * `Helper_de_Telemetria` (`emitEvent`/`emitMeasurement`), which remains free of
 * React and keeps the single allowlist-based privacy boundary (Req 11.3). The
 * helper is imported directly from `~/helpers/telemetry.helper` (rather than the
 * large `~/helpers` barrel) so this hook pulls in nothing beyond the telemetry
 * layer — both import paths are permitted by the task; the direct one keeps the
 * module lean.
 *
 * Everything is typed against the `Catálogo_de_Eventos` (`TelemetryEvent`); no
 * `any`. The public API type is inferred from the implementation
 * ({@link UseTelemetryApi}) so callers get full typing without hand-written
 * function-type signatures. Every emit path is gated on a mounted ref, so even
 * a stale reference captured by an async callback becomes a safe no-op once the
 * component is gone.
 */

import { useCallback, useEffect, useMemo, useRef } from "react";

import { emitEvent, emitMeasurement } from "~/helpers/telemetry.helper";
import type { TelemetryEvent } from "~/types/telemetry";

/**
 * High-resolution, monotonic clock used to measure flow durations. Prefers
 * `performance.now()` (monotonic, immune to wall-clock adjustments); falls back
 * to `Date.now()` only in the unlikely event the Performance API is absent.
 */
function nowMs(): number {
  return typeof performance !== "undefined" &&
    typeof performance.now === "function"
    ? performance.now()
    : Date.now();
}

/** No-op cancel handle returned when scheduling is refused (already unmounted). */
const NOOP_CANCEL = (): void => {
  // Nothing was scheduled, so there is nothing to cancel.
};

/**
 * React hook exposing the lifecycle-aware telemetry API. Holds flow start
 * markers and pending timers in refs, and installs a single unmount cleanup
 * that flips a mounted flag, clears all pending timers, and drops any in-flight
 * flow markers — the correctness property behind Req 11.9.
 *
 * @returns A referentially stable object (see {@link UseTelemetryApi}) whose
 * members are safe to call at any point in the lifecycle; after unmount they
 * are no-ops. Because the identity is memoized, it is safe to list in effect
 * dependency arrays in downstream tasks (5.12–5.17).
 */
export function useTelemetry() {
  /**
   * `false` once the component has unmounted. Every emit path checks this so a
   * stale reference held by an async callback cannot emit post-unmount, on top
   * of the timer clearing below. Reset to `true` on (re)mount to stay correct
   * under React StrictMode's mount→unmount→mount cycle.
   */
  const isMountedRef = useRef<boolean>(true);
  /** Start timestamps (ms) of in-flight flows, keyed by flow identifier. */
  const flowStartsRef = useRef<Map<string, number>>(new Map());
  /** Handles of scheduled emissions still pending, cleared on unmount. */
  const pendingTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(
    new Set(),
  );

  useEffect(() => {
    isMountedRef.current = true;
    const flowStarts = flowStartsRef.current;
    const pendingTimers = pendingTimersRef.current;
    return () => {
      // Correctness property (Req 11.9): after unmount, no event is emitted.
      // 1) Flip the guard so any late/async emit call becomes a no-op.
      isMountedRef.current = false;
      // 2) Cancel every scheduled emission so no deferred timer ever fires.
      for (const timer of pendingTimers) {
        clearTimeout(timer);
      }
      pendingTimers.clear();
      // 3) Drop in-flight flow markers so nothing lingers to be emitted.
      flowStarts.clear();
    };
  }, []);

  /**
   * Emits a discrete catalog event immediately (delegates to `emitEvent`).
   * No-op after unmount (Req 11.9) and, via the helper, when Faro is off.
   */
  const emit = useCallback((event: TelemetryEvent): void => {
    if (!isMountedRef.current) {
      return;
    }
    emitEvent(event);
  }, []);

  /**
   * Emits an ad-hoc numeric measurement (delegates to `emitMeasurement`).
   * Numeric-only by contract, so values cannot carry PII/report content.
   * No-op after unmount (Req 11.9).
   */
  const measure = useCallback(
    (name: string, values: Record<string, number>): void => {
      if (!isMountedRef.current) {
        return;
      }
      emitMeasurement(name, values);
    },
    [],
  );

  /**
   * Marks the start of a named flow, recording a high-resolution timestamp.
   * Calling it again for the same key restarts the measurement. No-op after
   * unmount.
   */
  const startFlow = useCallback((flowKey: string): void => {
    if (!isMountedRef.current) {
      return;
    }
    flowStartsRef.current.set(flowKey, nowMs());
  }, []);

  /**
   * Ends a flow started with {@link startFlow}, returning the elapsed duration
   * in whole milliseconds. Returns `null` when the flow was never started or
   * the component has already unmounted. The caller composes the catalog event
   * (or measurement) from the returned duration and emits it through {@link emit}
   * / {@link measure}, keeping emission on the single mounted-gated path.
   */
  const endFlow = useCallback((flowKey: string): number | null => {
    const start = flowStartsRef.current.get(flowKey);
    if (start === undefined) {
      return null;
    }
    flowStartsRef.current.delete(flowKey);
    // Never report a duration for a flow that ends after unmount (Req 11.9).
    if (!isMountedRef.current) {
      return null;
    }
    return Math.max(0, Math.round(nowMs() - start));
  }, []);

  /**
   * Discards a started flow without measuring or emitting anything (e.g. the
   * flow was aborted). Safe to call for an unknown key.
   */
  const cancelFlow = useCallback((flowKey: string): void => {
    flowStartsRef.current.delete(flowKey);
  }, []);

  /**
   * Schedules a catalog event to be emitted after `delayMs`. The pending timer
   * is tracked and cleared on unmount, and the deferred emission itself is
   * gated on the mounted ref, so it can never fire after the component is gone
   * (Req 11.9). Returns a function that cancels the scheduled emission early.
   */
  const scheduleEmit = useCallback(
    (event: TelemetryEvent, delayMs: number): (() => void) => {
      if (!isMountedRef.current) {
        return NOOP_CANCEL;
      }
      const timers = pendingTimersRef.current;
      const timer = setTimeout(() => {
        timers.delete(timer);
        // Re-check on fire: a race where cleanup ran between scheduling and
        // firing must not emit (Req 11.9).
        if (!isMountedRef.current) {
          return;
        }
        emitEvent(event);
      }, Math.max(0, delayMs));
      timers.add(timer);
      return () => {
        if (timers.delete(timer)) {
          clearTimeout(timer);
        }
      };
    },
    [],
  );

  return useMemo(
    () => ({ emit, measure, startFlow, endFlow, cancelFlow, scheduleEmit }),
    [emit, measure, startFlow, endFlow, cancelFlow, scheduleEmit],
  );
}

/**
 * Public shape of the telemetry API returned by {@link useTelemetry}, inferred
 * from the implementation so it never drifts from it and introduces no
 * hand-written function-type signatures. Downstream tasks (5.12–5.17) can
 * reference this type when passing the API around.
 */
export type UseTelemetryApi = ReturnType<typeof useTelemetry>;
